import type {
  BoundaryPoint,
  MapViewportProjector,
  ScreenPoint,
} from "context/PlotMap/geo";
import { edgeScreenAngleDegrees } from "context/PlotMap/gridMeshSlide";
import { rotationCellsForCornerVertex } from "context/PlotMap/gridMeshTopology";
import type {
  FrozenEdgeSlideConstraint,
  GridEdgeRef,
  GridMesh,
} from "context/PlotMap/gridMeshTypes";

import {
  cellsIntersectingBbox,
  findCellAtPoint,
  findEdgeAtScreenPoint,
  findMeshVertexIdAtScreenPoint,
} from "./gridMeshHitTest";

/** CSS rotate cursor (circular arrow) as a data-URL. */
export const ROTATE_CURSOR = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%23ffffff" stroke="%23000" stroke-width="1.2" d="M12 6a6 6 0 1 1-5.3 3.2l1.7.6A4.2 4.2 0 1 0 12 7.8V6zm-1 0V2l4 3.5L11 9V6z"/></svg>',
)}") 12 12, grab`;

/** Pixels of movement before a cell/edge mousedown becomes a drag (vs click). */
export const CELL_DRAG_THRESHOLD_PX = 4;

export type MeshPointerTarget =
  | { type: "vertex"; vertexId: string }
  | { type: "cell"; cellId: string; preferOverEdge: boolean }
  | { type: "edge"; edge: GridEdgeRef }
  | { type: "empty"; shiftHeld: boolean };

export type PendingCellDrag = {
  cellIds: string[];
  lastLatitude: number;
  lastLongitude: number;
  startX: number;
  startY: number;
};

export type PendingEdgeDrag = {
  chainVertexIds: string[];
  edge: GridEdgeRef;
  frozenConstraint: FrozenEdgeSlideConstraint | null;
  lastLatitude: number;
  lastLongitude: number;
  mode: "move" | "slide";
  startX: number;
  startY: number;
};

/**
 * Ordered mesh hit-test used by hover, mousedown, and click suppress checks:
 * vertex → edge → (shift+cell interior) → cell → empty.
 *
 * Edges win even with Shift held so Shift+drag on a border targets the edge
 * (free move) instead of cell multi-select. Shift+cell only applies in the cell
 * interior (no edge under the pointer) so multi-select / cell drag still work
 * away from borders.
 */
export const resolveMeshPointerTarget = (
  map: MapViewportProjector,
  mesh: GridMesh,
  screenPoint: ScreenPoint,
  geoPoint: BoundaryPoint,
  options: { shiftHeld: boolean },
): MeshPointerTarget => {
  const vertexId = findMeshVertexIdAtScreenPoint(
    map,
    mesh.vertices,
    screenPoint,
  );
  if (vertexId != null) {
    return { type: "vertex", vertexId };
  }

  const edge = findEdgeAtScreenPoint(map, mesh, screenPoint);
  if (edge != null) {
    return { type: "edge", edge };
  }

  const cellId = findCellAtPoint(mesh, geoPoint);
  if (cellId != null) {
    return {
      type: "cell",
      cellId,
      preferOverEdge: options.shiftHeld,
    };
  }

  return { type: "empty", shiftHeld: options.shiftHeld };
};

/** Hover cursor. Vertices and edges slide by default; Shift switches both to a free move. */
export const hoverCursorForMeshPointer = (
  map: MapViewportProjector,
  mesh: GridMesh,
  target: MeshPointerTarget,
  shiftHeld: boolean,
  rotateHeld: boolean,
): string | undefined => {
  if (target.type === "vertex") {
    if (
      rotateHeld &&
      rotationCellsForCornerVertex(mesh, target.vertexId) != null
    ) {
      return ROTATE_CURSOR;
    }
    // Shift matches edges: free move. A plain hover slides along a connected edge.
    return shiftHeld ? "move" : "pointer";
  }

  if (target.type === "cell") {
    return "pointer";
  }

  if (target.type === "edge") {
    if (shiftHeld) {
      return "move";
    }

    const angle = Math.abs(edgeScreenAngleDegrees(map, mesh, target.edge));
    const normalized = ((angle % 180) + 180) % 180;
    return normalized > 45 && normalized < 135 ? "col-resize" : "row-resize";
  }

  return target.shiftHeld ? "crosshair" : undefined;
};

export const createPendingCellDrag = (
  cellIds: string[],
  pointer: BoundaryPoint,
  screenPoint: ScreenPoint,
): PendingCellDrag => ({
  cellIds,
  lastLatitude: pointer.latitude,
  lastLongitude: pointer.longitude,
  startX: screenPoint.x,
  startY: screenPoint.y,
});

export const cellsInBboxSelection = (
  mesh: GridMesh,
  start: BoundaryPoint,
  end: BoundaryPoint,
): string[] => {
  const minLng = Math.min(start.longitude, end.longitude);
  const maxLng = Math.max(start.longitude, end.longitude);
  const minLat = Math.min(start.latitude, end.latitude);
  const maxLat = Math.max(start.latitude, end.latitude);

  return cellsIntersectingBbox(mesh, [
    [minLng, minLat],
    [maxLng, maxLat],
  ]);
};
